import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BackButton from "../../Components/BackButton";
import { Success, Error } from "../../Components/Modal";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { categorySchema } from "../../Schema/generalSchema";
import { useMutation, useQuery } from "react-query";
import { editCategory, getCategoryDetails } from "../../Services/Category";
import { image_url } from "../../Utils/connection_strings";
import CategoryForm from "./Components/CategoryForm";
import "../CMSManagement/CMSManagement.css";
import "../../styles/PremiumCardPages.css";

export default function EditCategory() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [image, setImage] = useState(null);
    const [preview, setPreview] = useState(null);

    const formMethods = useForm({
        resolver: yupResolver(categorySchema),
    });

    const { register, handleSubmit, formState: { errors }, control, reset, watch, setValue } = formMethods;

    const { isLoading: isFetching } = useQuery(
        ["category-details", id],
        () => getCategoryDetails(id),
        {
            enabled: !!id,
            refetchOnWindowFocus: false,
            onSuccess: (res) => {
                const category = res.data?.data?.docs?.[0];
                if (category) {
                    reset({
                        categoryName: category.categoryName,
                        description: category.description,
                        status: String(category.status),
                        estBaseWeight: category.estBaseWeight,
                        isFragile: !!category.isFragile,
                        isHeavy: !!category.isHeavy,
                    });
                    if (category.categoryImage && !image) {
                        const clean_url = image_url?.endsWith("/") ? image_url.slice(0, -1) : image_url;
                        const fullUrl = category.categoryImage.startsWith("http")
                            ? category.categoryImage
                            : `${clean_url}/${category.categoryImage}`;
                        setPreview(fullUrl);
                    }
                }
            },
            onError: () => Error("Could not retrieve category data. Please try again."),
        }
    );

    const { mutate, isLoading: isUpdating } = useMutation(editCategory, {
        onSuccess: (res) => {
            Success("Update Successful", res.data?.message || "Category specifications have been updated.");
            navigate("/category-management");
        },
        onError: (err) => Error(err?.response?.data?.message || "Communication failure during update."),
    });

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImage(file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const onSubmit = (data) => {
        const formData = new FormData();
        formData.append("typeName", "categoryImage");
        formData.append("id", id);
        Object.keys(data).forEach((key) => formData.append(key, data[key]));
        if (image) formData.append("categoryImage", image);
        mutate(formData);
    };

    return (
        <div className="app-content content pform-page">
            <div className="content-wrapper">
                <div className="content-body">
                    <section id="edit-category">
                        <div className="user-details-wrapper">
                            <div className="pform-page-header">
                                <div className="pform-page-header__row">
                                    <BackButton />
                                    <h1 className="pform-page-header__title">Edit Category Profile</h1>
                                </div>
                                <p className="pform-page-header__subtitle">
                                    Update category details, image, and logistics handling rules
                                </p>
                            </div>

                            <div className="pform-shell">
                                {isFetching ? (
                                    <div className="pform-loading">
                                        <div className="pform-loading__spinner" />
                                        <span>Loading category data...</span>
                                    </div>
                                ) : (
                                    <CategoryForm
                                        register={register}
                                        handleSubmit={handleSubmit}
                                        errors={errors}
                                        control={control}
                                        onSubmit={onSubmit}
                                        isLoading={isUpdating}
                                        isEdit
                                        preview={preview}
                                        handleImageChange={handleImageChange}
                                        isFragile={watch("isFragile")}
                                        isHeavy={watch("isHeavy")}
                                        setValue={setValue}
                                    />
                                )}
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}
